import { AfterViewChecked, Component, ElementRef, Inject, OnDestroy, ViewChild } from '@angular/core';
import { BehaviorSubject, Observable, ReplaySubject } from 'rxjs';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material';
import { MatDialog } from '@angular/material/dialog';
import { finalize, takeUntil } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';
import { Router } from '@angular/router';
import { FormControl } from '@angular/forms';

import {
  DataImportEntityGroupEnum,
  DataImportResponseModel,
  DataImportResponseRowModel
} from 'projects/workspace/src/app/data-import/models';
import { DataImportApiService } from 'projects/workspace/src/app/data-import/data-import-api.service';
import { PaginationModel } from 'common/src/models';
import { ToasterService } from 'common/src/modules/ui-components/toaster';
import { TableColumnModelExtended } from 'common/src/models/table-column.model';
import { TableSummaryBarItemModel } from 'common/src/modules/ui-components/table-summary-bar/table-summary-bar.model';
import { getColumnsByImportType, getTableSummaryBarItems } from './data-import-modal.config';
import { CommonModalsActionsEnum, InfoModalComponent, WarningModalComponent } from 'common/src/modules/modals/modals-common';
import { ProductTypes } from 'common/src/modules/products/product-types';
import { PaymentDirectionEnum } from 'projects/workspace/src/app/payment/models/payment.model';

@Component({
  selector: 'rnpl-data-import-modal',
  templateUrl: './data-import-modal.component.html',
  styleUrls: ['./data-import-modal.component.scss']
})
export class DataImportModalComponent implements OnDestroy, AfterViewChecked {

  private currentUploadedFile: File = null;
  public columns: TableColumnModelExtended[] = [];
  public rows: any = [];
  public importValidatedResponse: DataImportResponseModel = null;
  public tableSummaryBarItems: TableSummaryBarItemModel[] = [];
  public pagination: PaginationModel = { page: 0, per_page: 100, count: 100 };
  public glProductTypeIdControl = new FormControl();
  public dataImportEntityGroupEnum = DataImportEntityGroupEnum;

  @ViewChild('UploadFileInput', {static: true}) public uploadFileInputEl: ElementRef;

  readonly btnToClearLoadingStatus$: BehaviorSubject<string> = new BehaviorSubject<string>(null);
  readonly submitRequest$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  readonly uploadingProcessing: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  readonly destroy$: ReplaySubject<boolean> = new ReplaySubject<boolean>(1);

  constructor(
    private dataImportApiService: DataImportApiService,
    private toasterService: ToasterService,
    private translateService: TranslateService,
    private dialog: MatDialog,
    private router: Router,
    public readonly dialogRef: MatDialogRef<DataImportModalComponent>,
    @Inject(MAT_DIALOG_DATA) public data: {
      currentImportEntityGroup: DataImportEntityGroupEnum,
      currentProductTypeData: {productType: ProductTypes, glProductTypeId: number},
      salesPriceListControl: FormControl,
      validationResponse?: DataImportResponseModel,
      uploadedFile: File,
    }
  ) {
    if (this.data && this.data.currentProductTypeData && this.data.currentProductTypeData.glProductTypeId) {
      this.glProductTypeIdControl.patchValue(this.data.currentProductTypeData.glProductTypeId);
    }
    if (this.data && (this.data.validationResponse || this.data.uploadedFile)) {
      this.currentUploadedFile = this.data.uploadedFile;
      this.handleValidationResponse(this.data.validationResponse);
    }
    this.dialogRef.keydownEvents().subscribe(event => {
      if (event.key === 'Escape') {
        this.dialogRef.close();
      }
    });
  }

  ngAfterViewChecked(): void {
    if (this.data && this.data.validationResponse) {
      window.dispatchEvent(new Event('resize'));
    }
  }

  public uploadFile(filesList: FileList): void {
    if (!filesList || !filesList.length) { return; }
    if (filesList.length > 1) {
      this.toasterService.notify({type: 'error', message: 'DATA_IMPORT.SINGLE_FILE_ERROR'});
      return;
    }

    const file: File = filesList[0];
    const availableFormats = [
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'text/xml',
    ];

    if (!availableFormats.includes(file.type)) {
      this.toasterService.notify({type: 'error', message: 'DATA_IMPORT.FILE_TYPE_ERROR'});
      return;
    }

    this.currentUploadedFile = file;
    this.uploadFileInputEl.nativeElement.value = null;
    this.postUploadedFile();
  }

  private getImportRequest$(
    strategy: 'validation'|'import' = 'validation',
    importId?: number,
    force = false,
    glProductTypeId?: number
  ): Observable<DataImportResponseModel> {
    let importRequest$: Observable<any>;

    switch (this.currentImportEntityGroup) {
      case DataImportEntityGroupEnum.PRODUCTS:
        importRequest$ = this.dataImportApiService.importProducts(this.currentUploadedFile, strategy, importId, force, glProductTypeId);
        break;
      case DataImportEntityGroupEnum.PARTNERS:
        importRequest$ = this.dataImportApiService.importPartners(this.currentUploadedFile, strategy, importId, force);
        break;
      case DataImportEntityGroupEnum.PRICES:
        importRequest$ = this.dataImportApiService.importProductsPrices(
          this.currentUploadedFile,
          strategy,
          this.data.salesPriceListControl.value,
          importId,
          force
        );
        break;
      case DataImportEntityGroupEnum.STOCK:
        importRequest$ = this.dataImportApiService.importProductsStock(this.currentUploadedFile, strategy, importId, force);
        break;
      case DataImportEntityGroupEnum.PAYMENTS:
        force = strategy === 'import';
        importRequest$ = this.dataImportApiService.importPayments(this.currentUploadedFile, force);
        break;
      case DataImportEntityGroupEnum.PARTNER_ACCOUNT:
        force = strategy === 'import';
        importRequest$ = this.dataImportApiService.importPartnersAccounts(this.currentUploadedFile, force);
        break;
    }

    return importRequest$;
  }

  public postUploadedFile(strategy: 'validation'|'import' = 'validation', importId?: number, force = false): void {
    const importRequest$: Observable<DataImportResponseModel> = this.getImportRequest$(
      strategy,
      importId,
      force,
      this.glProductTypeIdControl.value
    );

    if (!importRequest$ || this.submitRequest$.getValue() || this.uploadingProcessing.getValue()) {
      return;
    }

    if (
      strategy === 'import'
      && this.currentImportEntityGroup === DataImportEntityGroupEnum.PAYMENTS
      && this.importValidatedResponse
      && this.importValidatedResponse.errorsCount
      && !this.importValidatedResponse.readyCount
    ) {
      this.openNothingImportedModal();
      return;
    }

    if (
      strategy === 'import'
      && !force
      && this.currentImportEntityGroup === DataImportEntityGroupEnum.PARTNER_ACCOUNT
      && this.importValidatedResponse
      && this.importValidatedResponse.rows.some(r => r.hasEmptyExternalAccountsWarning)
    ) {
      this.openWarningEmptyAccountsModal(strategy, importId);
      return;
    }

    if (importId) { // import strategy
      this.submitRequest$.next(true);
    } else { // validation strategy
      this.importValidatedResponse = null;
      this.uploadingProcessing.next(true);
    }

    this.toasterService.notifyRequestMessage({key: 'importedFileProcessing', message: 'DATA_IMPORT.FILE_PROCESSING'});

    importRequest$
      .pipe(
        finalize(() => {
          this.submitRequest$.next(false);
          this.uploadingProcessing.next(false);
          this.toasterService.hideRequestMessage('importedFileProcessing');
        }),
        takeUntil(this.destroy$)
      )
      .subscribe((response: DataImportResponseModel) => {
        if (strategy === 'import') {
          if (!response) {
            this.openNothingImportedModal();
          } else {
            // this.openSuccessModal(response.readyCount, response.entityType);
            if (
              this.currentImportEntityGroup !== DataImportEntityGroupEnum.PAYMENTS
              && this.currentImportEntityGroup !== DataImportEntityGroupEnum.PARTNER_ACCOUNT
            ) {
              this.router.navigate(['data-import/history']);
            }
            if (this.currentImportEntityGroup === DataImportEntityGroupEnum.PAYMENTS && response.readyCount) {
              const msg = this.translateService.instant('DATA_IMPORT.PAYMENTS_IMPORT_SUCCESS', {count: response.readyCount});
              this.toasterService.notifySuccess(msg);
            }
          }
          this.dialogRef.close(true);
          return;
        }

        if (strategy === 'validation') {
          if (this.currentImportEntityGroup === DataImportEntityGroupEnum.PAYMENTS && response.readyCount && !response.errorsCount) {
            const msg = this.translateService.instant('DATA_IMPORT.PAYMENTS_IMPORT_SUCCESS', {count: response.readyCount});
            this.toasterService.notifySuccess(msg);
            this.dialogRef.close();
            return;
          }
        }
        this.handleValidationResponse(response);
      }, error => {
        if (error.error.message === 'importValidationsUpdated') {
          this.openImportValidationsUpdatedModal(strategy, importId);
          return;
        }

        if (error.error && error.error.message) {
          this.toasterService.notifyError(error.error.message);
        }
        if (error.error && error.error.errors) {
          this.toasterService.notifyError(error.error.errors);
        }
      });
  }

  private handleValidationResponse(response: DataImportResponseModel): void {
    this.importValidatedResponse = response;
    this.columns = getColumnsByImportType(response.entityType);
    this.rows = this.prepareRows(response.rows);
    this.pagination.per_page = response.rowsCount;
    this.pagination.count = response.rowsCount;
    this.tableSummaryBarItems = getTableSummaryBarItems(response);
  }

  private openImportValidationsUpdatedModal(strategy: 'validation'|'import' = 'validation', importId: number): void {
    const dialog = this.dialog.open(WarningModalComponent, {
      data: {
        title: 'DATA_IMPORT.IMPORT_WARNING_MODAL_TITLE',
        message: 'DATA_IMPORT.IMPORT_WARNING_MODAL_MSG',
        confirmBtnText: 'DATA_IMPORT.IMPORT',
        confirmBtnIcon: 'cloud-upload'
      }
    });

    dialog.afterClosed().subscribe(res => {
      if (res === CommonModalsActionsEnum.CONFIRM) {
        this.postUploadedFile(strategy, importId, true);
      }
    });
  }

  private openNothingImportedModal(): void {
    this.dialog.open(InfoModalComponent, {
      data: {
        title: 'DATA_IMPORT.NOTHING_MODAL_TITLE',
        message:  'DATA_IMPORT.NOTHING_MODAL_MSG',
        confirmBtnText: 'BUTTON.CLOSE',
        hideCancelBtn: true
      }
    });
  }

  private openWarningEmptyAccountsModal(strategy: 'validation'|'import' = 'validation', importId: number): void {
    const dialog = this.dialog.open(WarningModalComponent, {
      data: {
        title: 'DATA_IMPORT.IMPORT_ACCOUNTS',
        message: 'DATA_IMPORT.EMPTY_ACCOUNTS_MODAL_MSG',
        confirmBtnText: 'BUTTON.CONTINUE',
        confirmBtnIcon: 'arrow-right'
      }
    });

    dialog.afterClosed().subscribe(res => {
      if (res === CommonModalsActionsEnum.CONFIRM) {
        this.postUploadedFile(strategy, importId, true);
      }
    });
  }

  // private openSuccessModal(readyCount: number, entityType: DataImportEntityEnum): void {
  //   this.dialog.open(InfoModalComponent, {
  //     data: {
  //       title: 'DATA_IMPORT.SUCCESS_MODAL_TITLE',
  //       message: this.translateService.instant('DATA_IMPORT.SUCCESS_MODAL_MSG', {quantity: readyCount}),
  //       titleColor: 'green-400',
  //       titleIcon: 'checkmark-circle',
  //       cancelBtnText: SUCCESS_MODAL_BTN_CONFIG[entityType].title,
  //       cancelBtnIcon: 'external-link',
  //       confirmBtnText: 'BUTTON.CLOSE',
  //       // confirmBtnIcon: 'close'
  //     }
  //   })
  //     .afterClosed()
  //     .subscribe(res => {
  //       if (res === CommonModalsActionsEnum.REJECT) {
  //         window.open(window.location.origin + [SUCCESS_MODAL_BTN_CONFIG[entityType].link], '_blank');
  //       }
  //     });
  // }

  private prepareRows(rows: DataImportResponseRowModel[]): DataImportResponseRowModel[] {
    return rows.map((r: DataImportResponseRowModel) => {

      const validations = r.validations;
      const warnings = r.warnings;

      // set class and amount sign for payment amount
      if (this.currentImportEntityGroup === DataImportEntityGroupEnum.PAYMENTS && r.hasOwnProperty('amount')) {
        r = {
          ...r,
          customClass: {
            ...r['customClass'],
            ['amount']: r.direction === PaymentDirectionEnum.OUTGOING
              ? 'color-danger-red'
              : 'color-green-600'
          },
          // amount: (r.direction === PaymentDirectionEnum.OUTGOING && +r.amount > 0)
          //   ? '-' + r.amount
          //   : r.amount
        };
      }

      for (const key in validations) {
        if (validations.hasOwnProperty(key) && validations[key].length) {
          r['customClass'] = {
            ...r['customClass'],
            [key]: 'color-danger-red'
          };

          r['rowStatusData'] = {
            ...r['rowStatusData'],
            errors: r['rowStatusData'] && r['rowStatusData']['errors']
              ? [...new Set([...r['rowStatusData']['errors'], ...validations[key]])]
              : validations[key]
          };
        }
      }

      for (const key in warnings) {
        if (warnings.hasOwnProperty(key) && warnings[key].length) {
          const hasErrors = r['rowStatusData'] && r['rowStatusData']['errors'] && r['rowStatusData']['errors'].length;
          // apply warnings only for rows without already applied errors
          if (!hasErrors) {
            r['rowStatusData'] = {
              ...r['rowStatusData'],
              warnings: r['rowStatusData'] && r['rowStatusData']['warnings']
                ? [...new Set([...r['rowStatusData']['warnings'], ...warnings[key]])]
                : warnings[key]
            };
          }
        }
      }

      r.rowStatusData = {
        ...r['rowStatusData'],
        icon: r.hasErrors
          ? 'icon-alert-triangle color-danger-red'
          : r.hasWarnings
            ? 'icon-alert-triangle color-yellow-600'
            : 'icon-checkmark-circle color-green-500',
      };

      return r;
    });
  }

  public get modalTitle(): string {
    let title = '';

    switch (this.currentImportEntityGroup) {
      case DataImportEntityGroupEnum.PRODUCTS:
        switch (this.data.currentProductTypeData.productType) {
          case ProductTypes.GOODS:
            title = 'DATA_IMPORT.IMPORT_PRODUCTS_GOODS';
            break;
          case ProductTypes.SERVICES:
            title = 'DATA_IMPORT.IMPORT_PRODUCTS_SERVICES';
            break;
          case ProductTypes.DIGITAL:
            title = 'DATA_IMPORT.IMPORT_PRODUCTS_DIGITAL';
            break;
        }
        break;
      case DataImportEntityGroupEnum.PARTNERS:
        title = 'DATA_IMPORT.IMPORT_PARTNERS';
        break;
      case DataImportEntityGroupEnum.PRICES:
        title = 'DATA_IMPORT.IMPORT_PRICES';
        break;
      case DataImportEntityGroupEnum.STOCK:
        title = 'DATA_IMPORT.IMPORT_STOCK_DATA';
        break;
      case DataImportEntityGroupEnum.PAYMENTS:
        title = 'DATA_IMPORT.PAYMENTS_IMPORT';
        break;
      case DataImportEntityGroupEnum.PARTNER_ACCOUNT:
        title = 'DATA_IMPORT.IMPORT_PARTNER_ACCOUNTS';
        break;
    }

    return title;
  }

  get currentImportEntityGroup(): DataImportEntityGroupEnum {
    return this.data.currentImportEntityGroup;
  }

  ngOnDestroy() {
    this.destroy$.next(true);
    this.destroy$.complete();
  }

}
